/** Fraction-aware length parser shared by every mm / inch input
 *  pair in the properties panel (rect / ellipse dimensions, slot
 *  width, offset distance, stitch fields, …).
 *
 *  HTML `type="number"` inputs can't accept slash characters, so the
 *  callers all use `type="text"` with `inputmode="decimal"` and route
 *  user input through this function instead of `parseFloat`. That
 *  way the same field accepts both:
 *
 *    1.5                 → 1.5
 *    1/2                 → 0.5
 *    1 1/2  /  1-1/2     → 1.5  (mixed fraction)
 *    -3/8                → -0.375
 *
 *  Explicit unit suffixes (#249) are supported when the caller
 *  passes `defaultUnit`. The numeric body is parsed as above, then
 *  the explicit unit is converted into the field's display unit so
 *  the user can type "1 in" into a millimetre field and the
 *  returned value (and the paired field's display) reflect 25.4 mm.
 *  Recognised suffixes (case-insensitive):
 *
 *    mm | millimeter(s) | millimetre(s)
 *    cm | centimeter(s) | centimetre(s)
 *    m  | meter(s)      | metre(s)
 *    in | inch | inches | " | ''
 *
 *  Without `defaultUnit`, suffixes are rejected and the parser
 *  behaves as a pure unit-less number reader (back-compat for
 *  callers that aren't yet aware of the unit context).
 *
 *  Returns `null` for empty / unparseable input so callers can use
 *  the absence-as-no-op pattern the existing helpers rely on. */

const MM_PER_UNIT = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
} as const;

export type LengthUnit = keyof typeof MM_PER_UNIT;

/** Strip a recognised unit suffix off the end of `body` (case-insensitive)
 *  and report which unit was found. Longer keywords win — "inches" matches
 *  before "in", "mm" before "m" — so "1mm" reads as 1 millimetre rather
 *  than 1 metre with an extra "m" left behind. */
function stripUnitSuffix(body: string): {
  unit: LengthUnit | null;
  remaining: string;
} {
  // Order matters: each pattern is tried in turn. Longer keywords first
  // (and the more-specific "mm"/"cm" before bare "m") so a 2-character
  // unit can't be mis-segmented into 1 character of unit + 1 character
  // of stray text.
  const patterns: Array<[RegExp, LengthUnit]> = [
    [/^(.*?)\s*(?:millimetres?|millimeters?|mm)$/i, "mm"],
    [/^(.*?)\s*(?:centimetres?|centimeters?|cm)$/i, "cm"],
    [/^(.*?)\s*(?:metres?|meters?|m)$/i, "m"],
    [/^(.*?)\s*(?:inches|inch|in|"|'')$/i, "in"],
  ];
  for (const [re, unit] of patterns) {
    const m = body.match(re);
    if (m) return { unit, remaining: m[1]!.trim() };
  }
  return { unit: null, remaining: body };
}

export function parseLengthInput(
  raw: string,
  defaultUnit?: LengthUnit,
): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  // Strip a leading minus once, parse the rest as a non-negative
  // value, then re-apply the sign. Lets the fraction parser stay
  // unsigned (so "-1 1/2" doesn't need its own grammar).
  let sign = 1;
  let body = trimmed;
  if (body.startsWith("-")) {
    sign = -1;
    body = body.slice(1).trim();
  } else if (body.startsWith("+")) {
    body = body.slice(1).trim();
  }
  if (body === "") return null;

  // Pull off a trailing unit suffix. Only honoured when the caller
  // told us what unit the field is in (`defaultUnit`) — without that
  // context, "1 in" would be ambiguous (1 what?), so reject it like
  // any other trailing garbage.
  let explicitUnit: LengthUnit | null = null;
  if (defaultUnit !== undefined) {
    const stripped = stripUnitSuffix(body);
    if (stripped.unit !== null) {
      explicitUnit = stripped.unit;
      body = stripped.remaining;
      if (body === "") return null;
    }
  }

  // Mixed fraction "W N/M" — whole number followed by a slash-form
  // fraction, separated by spaces and/or a single hyphen (the
  // common imperial shorthand "1-1/2"). Splits cleanly because the
  // whole part has no slash and the fraction part does.
  const mixed = body.match(
    /^(\d+(?:\.\d+)?)[\s-]+(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/,
  );
  let value: number | null = null;
  if (mixed) {
    const w = Number(mixed[1]);
    const n = Number(mixed[2]);
    const d = Number(mixed[3]);
    if (
      Number.isFinite(w) &&
      Number.isFinite(n) &&
      Number.isFinite(d) &&
      d !== 0
    ) {
      value = w + n / d;
    } else {
      return null;
    }
  } else {
    // Pure fraction "N/M".
    const frac = body.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
    if (frac) {
      const n = Number(frac[1]);
      const d = Number(frac[2]);
      if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) {
        value = n / d;
      } else {
        return null;
      }
    } else {
      // Plain decimal — same shape `parseFloat` accepted before but
      // stricter: only digits, optional decimal point, optional decimal
      // part. Rejects "1abc" → 1 fallthroughs that parseFloat would
      // have silently accepted.
      const decimal = body.match(/^\d+(?:\.\d+)?$|^\.\d+$/);
      if (decimal) {
        const n = Number(body);
        value = Number.isFinite(n) ? n : null;
      }
    }
  }
  if (value === null) return null;

  // Convert into the field's unit. The numeric body was typed in
  // `explicitUnit` (when given) or in `defaultUnit` (otherwise), and
  // we always return in `defaultUnit` so the caller can treat the
  // result as if the user had typed in their field's labelled unit.
  if (
    explicitUnit !== null &&
    defaultUnit !== undefined &&
    explicitUnit !== defaultUnit
  ) {
    value = (value * MM_PER_UNIT[explicitUnit]) / MM_PER_UNIT[defaultUnit];
  }
  return sign * value;
}
